'use client';

import Image from 'next/image';
import { useRef } from 'react';
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
  type MotionValue,
} from 'framer-motion';
import { cn } from '@/lib/utils';

/**
 * Section 4 — Own it. Or lose it. Figma node `79:1093` (page-level).
 *
 * Scroll-driven storyboard. A tall outer track (300vh) wraps a
 * `sticky top-0 h-screen` viewport so the inner stage stays pinned
 * for three viewport heights of scroll. `useScroll({ target, offset:
 * ['start start', 'end end'] })` gives us a normalized `0 → 1`
 * progress that we slice into three step windows (see STEP_FRAMES).
 *
 * Visual stack (from back to front):
 *   1. `/landing/own-it.png` — the *persistent* centred backdrop. Sits
 *      in the centre of the viewport across all three steps, with a
 *      small parallax drift so it feels alive without distracting from
 *      the panels.
 *   2. `/landing/price-your-land.png` — anchored to the LEFT side of
 *      the stage (slightly overlapping the centred board), fades in
 *      during step 1 and out before step 2. The only side image the
 *      section has for v1; steps 2 and 3 read against the centred
 *      own-it.png alone.
 *   3. The three floating text panels, cross-fading by scroll progress
 *      and alternating sides for visual rhythm.
 *
 * Mobile (`lg:hidden`) and `prefers-reduced-motion` viewers get a
 * static stack of the three panels with `own-it.png` as a quiet bed
 * behind them — pinned scroll storytelling depends on a tall viewport
 * and a desktop scroll surface; on phones the same content reads
 * better as a simple sequence.
 */
export function OwnItSection() {
  const trackRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ['start start', 'end end'],
  });
  const prefersReducedMotion = useReducedMotion();

  // Centre image: subtle parallax + scale "breathing" across the
  // pinned segment so it doesn't feel like a static photograph.
  const centerScale = useTransform(scrollYProgress, [0, 1], [1, 1.06]);
  const centerY = useTransform(scrollYProgress, [0, 1], ['-2%', '2%']);

  // Step-1 vignette image (price-your-land.png) — synced with the
  // step-1 panel: holds at full opacity through the first third of
  // the pin, then cross-fades out as step 2 takes over. Uses the same
  // window as STEP_FRAMES[0] so the image and its panel move as one.
  const stepImageOpacity = useTransform(
    scrollYProgress,
    [0, 0.30, 0.40, 1],
    [1, 1, 0, 0],
  );

  return (
    <section id="own-it" className="relative isolate bg-black">
      {/* Intro — heading + subhead, scrolls normally above the pinned
          storyboard. Padding deliberately tight so the pin engages
          shortly after the viewer enters the section; previously the
          intro was tall enough that the animation felt like it
          started ~half a viewport too late. */}
      <div className="mx-auto max-w-3xl px-6 pt-12 pb-4 text-center sm:px-8 lg:pt-16 lg:pb-6 lg:px-12">
        <h2
          className="font-marketing text-4xl uppercase tracking-[0.01em] text-amber-50 sm:text-5xl lg:text-[48px] lg:leading-[1.04]"
          style={{
            textShadow:
              '0 0 60px rgba(255, 215, 120, 0.18), 0 1px 0 rgba(0, 0, 0, 0.6)',
          }}
        >
          Own it. Or lose it.
        </h2>
        <p className="mx-auto mt-4 max-w-xl font-mono text-[12px] uppercase tracking-[0.135em] text-amber-100 sm:text-[14px]">
          Price land. Defend territory. Outplay the market.
        </p>
      </div>

      {/* Desktop — pinned scroll storyboard. Hidden on mobile and for
          users who prefer reduced motion (both get the static stack
          below). */}
      {!prefersReducedMotion && (
        <div
          ref={trackRef}
          className="relative hidden h-[300vh] lg:block"
        >
          <div className="sticky top-0 flex h-screen items-center overflow-hidden">
            {/* Persistent centre image — own-it.png stays visible
                across all three steps as the section's anchor. */}
            <motion.div
              style={{ scale: centerScale, y: centerY }}
              className="absolute inset-0 flex items-center justify-center"
              aria-hidden
            >
              <div className="relative h-[80%] w-[60%]">
                <Image
                  src="/landing/own-it.png"
                  alt=""
                  fill
                  sizes="60vw"
                  className="object-contain object-center"
                  priority={false}
                />
              </div>
            </motion.div>

            {/* Step-1 vignette image — price-your-land.png, fades in
                during step 1 only. Anchored to the LEFT side of the
                stage (at 4–7% from the viewport edge depending on
                width) and sized to roughly match the Figma overlay
                (~324×260) so it overlaps slightly with the centred
                board on its left edge. Desktop-only — the mobile
                fallback further down deliberately omits it. */}
            <motion.div
              style={{ opacity: stepImageOpacity }}
              className="pointer-events-none absolute left-[4%] top-[42%] h-[256px] w-[320px] -translate-y-1/2 xl:left-[7%] xl:h-[300px] xl:w-[374px]"
              aria-hidden
            >
              <Image
                src="/landing/price-your-land.png"
                alt=""
                fill
                sizes="(min-width: 1280px) 374px, 320px"
                className="object-contain object-left"
              />
            </motion.div>

            {/* Floating panels — alternate sides for rhythm: Step 1
                on the left (near the price-your-land image), Step 2
                on the right, Step 3 on the left again. */}
            <div className="relative mx-auto h-full w-full max-w-7xl px-12">
              {STEPS.map((step, i) => (
                <OwnItPanel
                  key={step.number}
                  step={step}
                  index={i}
                  scrollYProgress={scrollYProgress}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile + reduced-motion fallback — static stack of all three
          panels with own-it.png as a quiet bed behind them. Same copy,
          same numbered ordinals, no scroll choreography. */}
      <div className={cn('relative', !prefersReducedMotion && 'lg:hidden')}>
        <div className="relative px-6 py-12 sm:px-8 sm:py-16">
          <div className="absolute inset-0 -z-10 opacity-25">
            <Image
              src="/landing/own-it.png"
              alt=""
              fill
              sizes="100vw"
              className="object-contain object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/80 to-black" />
          </div>
          <div className="mx-auto flex max-w-2xl flex-col gap-6">
            {STEPS.map((step) => (
              <PanelCard key={step.number} step={step} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

interface Step {
  number: '01' | '02' | '03';
  title: string;
  body: string;
  /** Which side of the stage the text panel anchors to on desktop. */
  side: 'left' | 'right';
}

const STEPS: Step[] = [
  {
    number: '01',
    title: 'Price your land',
    body: "What's it worth to you? You set the number, in the board's tax token.",
    side: 'right',
  },
  {
    number: '02',
    title: 'Defend it',
    body: 'Price too low and someone takes it. Price too high and rent drains your balance.',
    side: 'left',
  },
  {
    number: '03',
    title: "Take someone else's",
    body: 'See an underpriced tile? Buy it and make it yours.',
    side: 'right',
  },
];

/** Scroll progress keyframes for each step. Each entry pairs
 *  `input` (scroll progress) with `opacity` and `y` (panel transform)
 *  outputs of matching length. Avoid degenerate input ranges
 *  (`[0, 0, ...]` or `[..., 1.0, 1.0]`) — useTransform behaves
 *  unpredictably at boundaries when two adjacent input values match,
 *  which caused Step 1 to flash back in at the very end of the
 *  scroll. Instead, Step 1 omits the fade-in and Step 3 omits the
 *  fade-out — useTransform clamps to the first/last output value
 *  outside the input range, so the panels stay correctly opaque or
 *  hidden at the edges. */
interface StepFrames {
  input: number[];
  opacity: number[];
  y: string[];
}

// Step windows are non-overlapping in their hold phases but share
// boundaries during fades, so as one step fades out the next fades in
// at the same time (true cross-fade). useTransform clamps outside the
// input range, so anchoring each step's outer keyframes guarantees it
// holds opacity 0 elsewhere and never flashes back in.
const STEP_FRAMES: StepFrames[] = [
  // Step 1 — visible at pin engagement, holds through the first
  // third, then cross-fades out from 0.30 → 0.40.
  {
    input: [0, 0.30, 0.40, 1],
    opacity: [1, 1, 0, 0],
    y: ['0px', '0px', '-20px', '-20px'],
  },
  // Step 2 — cross-fades in from 0.30 → 0.40, holds through the
  // middle third, then cross-fades out from 0.60 → 0.70.
  {
    input: [0, 0.30, 0.40, 0.60, 0.70, 1],
    opacity: [0, 0, 1, 1, 0, 0],
    y: ['20px', '20px', '0px', '0px', '-20px', '-20px'],
  },
  // Step 3 — cross-fades in from 0.60 → 0.70 and holds to the end
  // of the pinned scroll.
  {
    input: [0, 0.60, 0.70, 1],
    opacity: [0, 0, 1, 1],
    y: ['20px', '20px', '0px', '0px'],
  },
];

function OwnItPanel({
  step,
  index,
  scrollYProgress,
}: {
  step: Step;
  index: number;
  scrollYProgress: MotionValue<number>;
}) {
  const frames = STEP_FRAMES[index];
  const opacity = useTransform(scrollYProgress, frames.input, frames.opacity);
  const y = useTransform(scrollYProgress, frames.input, frames.y);

  return (
    <motion.div
      style={{ opacity, y }}
      className={cn(
        'absolute top-[42%] -translate-y-1/2',
        step.side === 'right' ? 'right-12' : 'left-12',
        'w-full max-w-[420px]',
      )}
    >
      <PanelCard step={step} />
    </motion.div>
  );
}

function PanelCard({ step }: { step: Step }) {
  return (
    <article className="relative rounded-[5px] border border-amber-500/25 bg-zinc-950/85 px-7 py-8 backdrop-blur-[6px]">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-7 rounded-t-[5px] bg-gradient-to-b from-white/[0.04] to-transparent"
      />
      <span className="absolute right-5 top-4 font-mono text-[10px] tracking-[0.2em] text-amber-400">
        {step.number}
      </span>
      <h3 className="font-marketing text-3xl uppercase leading-[1.1] tracking-[0.027em] text-amber-50 lg:text-[32px]">
        {step.title}
      </h3>
      <p className="mt-4 text-[13px] leading-[1.55] text-amber-100 lg:text-[14px] lg:leading-[1.5]">
        {step.body}
      </p>
    </article>
  );
}
